import { Box } from '@mui/material';
import type { CSSProperties } from 'react';

import type { CalendarEvent, Member } from '../types';
import { durationToPixels, formatTimeTile, hourToPixels } from '../utils';

type EventTileProps = {
  event: CalendarEvent;
  member?: Member;
  baseHour: number;
  slotHeight: number;
  isSelected?: boolean;
  isDragging?: boolean;
  hasConflict?: boolean;
  /** Zero-based column index within the day column (for overlap layout). */
  colIdx?: number;
  /** Total concurrent columns at this event's peak overlap. */
  numCols?: number;
  onClick?: (event: CalendarEvent) => void;
  // Drag handle wiring from useDraggable — kept as unknown to avoid coupling to @dnd-kit types
  dragRef?: (node: HTMLDivElement | null) => void;
  dragListeners?: Record<string, (...args: unknown[]) => unknown>;
  dragAttributes?: Record<string, string | boolean | number | undefined>;
};

const BLOCK_HATCH =
  'repeating-linear-gradient(45deg, #f0ede8, #f0ede8 3px, #e0ddd8 3px, #e0ddd8 6px)';
const OVERRIDE_HATCH =
  'repeating-linear-gradient(45deg, #fce8c8, #fce8c8 3px, #f5d89c 3px, #f5d89c 6px)';

// Outer gutter (px) on each side of the tile group within a column
const OUTER = 4;
// Gap (px) between adjacent overlapping tiles
const GAP = 2;

export function EventTile({
  event,
  member,
  baseHour,
  slotHeight,
  isSelected = false,
  isDragging = false,
  hasConflict = false,
  colIdx = 0,
  numCols = 1,
  onClick,
  dragRef,
  dragListeners,
  dragAttributes,
}: EventTileProps) {
  const top = hourToPixels(event.start, baseHour, slotHeight);
  const height = durationToPixels(event.end - event.start, slotHeight);

  const isBlock = event.kind === 'block';
  const isOverride = event.kind === 'override';
  const isSession = event.kind === 'session';

  const isOOO = isBlock && Boolean(event.meta?.isOOO);
  const isNoShow =
    isSession &&
    (event.meta?.attendanceStatus === 'no_show' ||
      event.meta?.attendanceStatus === 'member_no_show' ||
      event.meta?.attendanceStatus === 'coach_no_show' ||
      event.meta?.attendanceStatus === 'unattended');
  const isChemCall =
    isSession && (Boolean(event.meta?.isChemistryCall) || event.meta?.sessionType === 'Chemistry');

  const sessionColor = isNoShow ? '#bdbdbd' : (member?.color ?? '#5e8f3e');

  // ---- Positioning ----
  // For a single event: left=OUTER, right=OUTER (fills column)
  // For overlapping events: divide column into numCols equal strips with GAP between them
  const totalDeduction = OUTER * 2 + (numCols - 1) * GAP; // px taken by gutters+gaps
  const positionStyle: CSSProperties =
    numCols === 1
      ? { left: OUTER, right: OUTER }
      : {
          left: `calc(${OUTER}px + ${colIdx} * ((100% - ${totalDeduction}px) / ${numCols} + ${GAP}px))`,
          width: `calc((100% - ${totalDeduction}px) / ${numCols})`,
        };

  const style: CSSProperties = {
    position: 'absolute',
    top,
    height: Math.max(height - 2, 18),
    ...positionStyle,
    borderRadius: 6,
    padding: '3px 6px',
    fontSize: 11,
    overflow: 'hidden',
    zIndex: isBlock || isOverride ? 0 : 1,
    pointerEvents: isBlock || isOverride ? 'none' : 'auto',
    cursor: isDragging ? 'grabbing' : isSession ? 'grab' : 'default',
    userSelect: 'none',
    boxSizing: 'border-box',
    opacity: 1, // per-kind opacity set below
    touchAction: 'none',

    ...(isSession && {
      background: sessionColor,
      color: isNoShow ? '#757575' : '#fff',
      border: hasConflict ? '2px dashed #b58a1e' : '2px solid transparent',
      boxShadow: isNoShow
        ? 'none'
        : isSelected
          ? `inset 0 0 0 2px rgba(255,255,255,0.75), 0 2px 10px rgba(0,0,0,0.28)`
          : `0 1px 3px rgba(0,0,0,0.14)`,
      opacity: isNoShow ? 0.65 : isDragging ? 0.4 : 1,
    }),

    ...(isBlock && {
      background: BLOCK_HATCH,
      color: '#888',
      fontStyle: 'italic',
    }),

    ...(isOverride && {
      background: OVERRIDE_HATCH,
      color: '#8a6a20',
      fontStyle: 'italic',
    }),
  };

  const title = event.title ?? (isChemCall ? 'Chemistry Call' : (member?.name ?? ''));
  // Show time label when the tile is tall enough to hold a second line
  const showTime = isSession && !isNoShow && !isChemCall && height >= 22;

  return (
    <div
      ref={dragRef}
      style={style}
      {...dragListeners}
      {...dragAttributes}
      onClick={isSession ? () => onClick?.(event) : undefined}
      role={isSession ? 'button' : undefined}
      tabIndex={isSession ? 0 : undefined}
      onKeyDown={
        isSession
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') onClick?.(event);
            }
          : undefined
      }
    >
      {/* OOO label */}
      {isOOO && height >= 18 && (
        <Box
          sx={{
            fontSize: 9,
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
            opacity: 0.7,
            lineHeight: 1,
            mb: '2px',
          }}
        >
          Out of office
        </Box>
      )}

      {/* Override kind label only — blocks rely on the legend */}
      {isOverride && height >= 24 && (
        <Box
          sx={{
            fontSize: 9,
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
            opacity: 0.7,
            lineHeight: 1,
            mb: '2px',
          }}
        >
          Override
        </Box>
      )}

      {/* Title + start time (Google Calendar style: inline, time wraps below if needed) */}
      <Box sx={{ overflow: 'hidden', lineHeight: 1.3 }}>
        <Box component="span" sx={{ fontWeight: 500, fontSize: 11 }}>
          {title}
        </Box>
        {showTime && (
          <Box component="span" sx={{ fontWeight: 400, fontSize: 11, opacity: 0.88 }}>
            {' '}
            {formatTimeTile(event.start)}
          </Box>
        )}
      </Box>

      {/* No-show badge */}
      {isNoShow && height >= 18 && (
        <Box
          sx={{
            display: 'inline-block',
            background: 'rgba(255,255,255,0.7)',
            color: '#757575',
            fontSize: 9,
            px: '4px',
            py: '1px',
            borderRadius: '3px',
            mt: '2px',
            fontWeight: 600,
            border: '1px solid rgba(0,0,0,0.1)',
          }}
        >
          No show
        </Box>
      )}

      {/* Conflict chip */}
      {hasConflict && height >= 36 && (
        <Box
          sx={{
            display: 'inline-block',
            background: '#fcf3e0',
            color: '#8a6a20',
            fontSize: 9,
            px: '5px',
            py: '1px',
            borderRadius: '3px',
            mt: '2px',
            border: '1px solid #f5d89c',
          }}
        >
          Overlaps block
        </Box>
      )}
    </div>
  );
}
